// File: src/components/layouts/navigation/Sidebar/Sidebar.tsx
// Purpose: Layout or container component

import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { LogOut, Menu, X } from "lucide-react";
import { Button, Typography } from "../../../general";
import DefaultLogo from "../../../../assets/logo.svg";
import { SidebarEntry } from "../../bars";
import { Flex } from "../../../layouts/wrappers";

interface SidebarProps {
  links: { label: string; icon: React.ReactNode; path: string; notifications?: number }[];
  userProfile?: { name: string; avatar: string; onClick: () => void };
  onLogout?: () => void;
  compact?: boolean;
  sticky?: boolean;
  logo?: boolean;
}

const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const SidebarContainer = styled.aside<{ compact: boolean; mobileOpen: boolean; sticky?: boolean }>`
  width: ${({ compact }) => (compact ? "72px" : "260px")};
  height: 100vh;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  box-shadow: ${({ theme }) => theme.elevations.low};
  display: flex;
  flex-direction: column;
  position: ${({ sticky }) => (sticky ? "sticky" : "fixed")};
  top: 0;
  left: ${({ mobileOpen }) => (mobileOpen ? "0" : "-260px")}; /* Mobile drawer */
  z-index: 1100;
  transition: left 0.3s ease-in-out, width 0.3s ease-in-out;
  overflow: visible; /* Ensures tooltips can render outside the sidebar */

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    left: 0; /* Always visible on tablet & desktop */
  }
`;

const SidebarContent = styled.div<{ compact: boolean }>`
  width: ${({ compact }) => (compact ? "72px" : "260px")};
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.md};
  padding-inline: ${({ theme, compact }) => (compact ? "0" : theme.spacing.md)};
  transition: width 0.3s ease-in-out;
  position: relative;
  box-shadow: ${({ theme }) => theme.elevations.high};
`;

const SidebarHeader = styled.div<{ compact: boolean , logo: boolean}>`
  display: flex;
  align-items: center;
  justify-content: ${({ compact, logo }) => (compact ? "center" : !logo ? 'flex-end': "space-between")};
  width: 100%;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;

  img {
    height: ${({ compact }) => (compact ? "40px" : "48px")};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const SidebarLinksWrapper = styled.div<{ compact: boolean }>`
  flex-grow: 1;
  overflow-y: auto; /* Allow scrolling inside */
  overflow-x: hidden; /* ✅ Fix tooltip cutoff */
  display: flex;
  flex-direction: column;
  align-items: ${({ compact }) => (compact ? "center" : "flex-start")};
  gap: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.md};

  /* ✅ Allow tooltips to render properly */
  position: relative;
  z-index: 1300;

  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.divider};
    border-radius: ${({ theme }) => theme.radii.sm};
  }
`;

const SidebarFooter = styled.div<{ compact: boolean , mobileOpen: boolean }>`
  flex-shrink: 0;
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: ${({ mobileOpen, compact }) => (mobileOpen ? "flex-end" : compact ? "center" : "flex-start")};
`;

/* 🛠 Mobile Menu Backdrop */
const MobileBackdrop = styled.div<{ mobileOpen: boolean }>`
  display: ${({ mobileOpen }) => (mobileOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4); /* Dim overlay */
  z-index: 1090;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

/* 📱 Floating Button (ONLY in Mobile Mode) */
const MobileFloatingButton = styled(Button)`
  position: fixed;
  bottom: 16px;
  left: 16px;
  z-index: 1200;
  display: flex;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const Sidebar: React.FC<SidebarProps> = ({
  links,
  userProfile,
  onLogout,
  compact = false,
  sticky = false,
  logo = true,
}) => {
  const [isCompact, setIsCompact] = useState(compact);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Overlay for Mobile Mode */}
      <MobileBackdrop mobileOpen={mobileOpen} onClick={() => setMobileOpen(false)} />
      <MobileFloatingButton variant="secondary"  onClick={() => setMobileOpen(!mobileOpen)}>
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </MobileFloatingButton>

      {/* Sidebar Container */}
      <SidebarContainer compact={isCompact} mobileOpen={mobileOpen} sticky={sticky}>
        <SidebarContent compact={isCompact}>
          
          {/* Header */}
          <SidebarHeader compact={isCompact} logo={logo}>
            {!isCompact && logo && <img src={DefaultLogo} alt="Logo" />}
            <Button variant="ghost"  size={"compact"} onClick={() => setIsCompact(!isCompact)}>
              {isCompact ? <Menu size={20} /> : <X size={20} />}
            </Button>
          </SidebarHeader>

          {/* Sidebar Links */}
          <SidebarLinksWrapper compact={isCompact}>
            {links.map((link, index) => (
              <SidebarEntry
                key={index}
                label={link.label}
                icon={link.icon}
                path={link.path}
                count={link.notifications}
                compact={isCompact}
              />
            ))}
          </SidebarLinksWrapper>

          {/* Footer / Logout */}
          <SidebarFooter compact={isCompact} mobileOpen={mobileOpen}>
            {onLogout && (
              
              <Button fullWidth={(!mobileOpen && !isCompact)} variant="ghost" iconOnly={isCompact} icon={<LogOut size={20} />} size={isCompact ? "compact" : "medium"}  onClick={onLogout}>
                {!isCompact && <Typography  as="span" color='white'  variant="body">Logout</Typography>}
              </Button>

            )}
          </SidebarFooter>
        </SidebarContent>
      </SidebarContainer>
    </>
  );
};